/**
 * Ticket-type attribute helpers shared across consumer ticket and swap flows.
 *
 * Classification is attributes.type from the events service, never display name.
 */

const CONSUMER_FORBIDDEN_PAY_AT_DOOR_COPY = [
  "Pago en puerta",
  "A pagar en puerta",
  "Pago pendiente",
  "$0 en GRUV",
  "cobrar en puerta",
] as const;

/**
 * ticketTypeClassification returns the uppercase attributes.type, or empty.
 */
export function ticketTypeClassification(
  attributes?: Record<string, unknown> | null,
): string {
  const raw = attributes?.type;
  return typeof raw === "string" ? raw.trim().toUpperCase() : "";
}

/**
 * isComboTicketType returns true when ticket-type attributes declare a COMBO
 * (pack) type. Combo tickets cannot be listed on the swap market.
 */
export function isComboTicketType(
  attributes?: Record<string, unknown> | null,
): boolean {
  return ticketTypeClassification(attributes) === "COMBO";
}

/**
 * isZeroPaymentClaimTicket is FREE or PAY_AT_DOOR: claim via claim-free, no Mercado Pago.
 */
export function isZeroPaymentClaimTicket(
  attributes?: Record<string, unknown> | null,
): boolean {
  const type = ticketTypeClassification(attributes);
  return type === "FREE" || type === "PAY_AT_DOOR";
}

/**
 * isPayAtDoorTicket reports the explicit PAY_AT_DOOR classification.
 */
export function isPayAtDoorTicket(
  attributes?: Record<string, unknown> | null,
): boolean {
  return ticketTypeClassification(attributes) === "PAY_AT_DOOR";
}

/**
 * isResaleBlockedTicketType is the consumer-side Swap/auction eligibility filter.
 * Backend Swap still enforces this; UI hide is not the authority.
 */
export function isResaleBlockedTicketType(
  attributes?: Record<string, unknown> | null,
): boolean {
  const type = ticketTypeClassification(attributes);
  return type === "FREE" || type === "PAY_AT_DOOR" || type === "COMBO" || type === "INVITATION";
}

/**
 * isTransferBlockedTicketType is PAY_AT_DOOR only. FREE remains transferable.
 */
export function isTransferBlockedTicketType(
  attributes?: Record<string, unknown> | null,
): boolean {
  return isPayAtDoorTicket(attributes);
}

/**
 * isFreeClaimTicket is classic FREE only (1-claim, GRATIS). PAY_AT_DOOR is not.
 */
export function isFreeClaimTicket(
  attributes?: Record<string, unknown> | null,
): boolean {
  return ticketTypeClassification(attributes) === "FREE";
}

/**
 * remainingClaimableTickets converts remaining held credits into remaining
 * tickets. FREE is 1 credit per ticket. PAY_AT_DOOR divides by creditsPerUnit.
 */
export function remainingClaimableTickets(
  attributes: Record<string, unknown> | null | undefined,
  remainingCredits: number,
): number {
  const type = ticketTypeClassification(attributes);
  if (!Number.isFinite(remainingCredits) || remainingCredits < 1) {
    return 0;
  }
  if (type === "FREE") {
    return remainingCredits;
  }
  if (type === "PAY_AT_DOOR") {
    const unit = Number(attributes?.creditsPerUnit);
    if (!Number.isFinite(unit) || unit < 1 || remainingCredits < unit) {
      return 0;
    }
    return Math.floor(remainingCredits / unit);
  }
  return 0;
}

/**
 * containsForbiddenPayAtDoorCopy is used in tests to keep Consumer generic.
 */
export function containsForbiddenPayAtDoorCopy(value: string): boolean {
  return CONSUMER_FORBIDDEN_PAY_AT_DOOR_COPY.some((needle) => value.includes(needle));
}

